// Tenant-portal i18n — EN/ES. Deliberately tiny: a dictionary + t() and a
// persisted language preference. The portal is the surface Spanish-speaking
// tenants actually touch; the operator app stays English.
// Coverage grows string-by-string — untranslated keys fall through to English.
import { useSyncExternalStore } from 'react';

const LS_KEY = 'tenantPortalLang';

const ES = {
  // Login
  'Tenant Portal': 'Portal del inquilino',
  'Sign in': 'Iniciar sesión',
  'Email': 'Correo electrónico',
  'Password': 'Contraseña',
  'Send me a code': 'Enviarme un código',
  'Enter the 6-digit code we sent you': 'Ingrese el código de 6 dígitos que le enviamos',
  'Verify code': 'Verificar código',
  'Sign in with password': 'Iniciar sesión con contraseña',
  'Sign in with a code instead': 'Iniciar sesión con un código',

  // Dashboard chrome
  'My Units': 'Mis unidades',
  'Payments': 'Pagos',
  'Invoices': 'Facturas',
  'Documents': 'Documentos',
  'Contact Us': 'Contáctenos',
  // A rental the office has paused while it confirms a reported move-out.
  'We are confirming your move-out.': 'Estamos confirmando su mudanza.',
  'Billing and reminders for this unit are on hold while we check.':
    'La facturación y los recordatorios de esta unidad están en pausa mientras verificamos.',
  'You can still pay any balance shown here.':
    'Aún puede pagar cualquier saldo que aparezca aquí.',
  'My Info': 'Mi información',
  'Sign out': 'Cerrar sesión',
  'Account Balance': 'Saldo de la cuenta',
  'Autopay': 'Pago automático',
  'On': 'Activado',
  'Off': 'Desactivado',
  'Set up': 'Configurar',
  'Turn off': 'Desactivar',
  'Update card': 'Actualizar tarjeta',
  'Active Units': 'Unidades activas',

  // Units tab
  'No rentals found.': 'No se encontraron alquileres.',
  'Rate:': 'Tarifa:',
  'Started:': 'Inicio:',
  'Balance:': 'Saldo:',
  'Next due:': 'Próximo vencimiento:',
  'Sign lease': 'Firmar contrato',
  'View lease': 'Ver contrato',
  'Pay all': 'Pagar todo',
  'Pay everything in one step.': 'Pague todo en un solo paso.',
  '+ Rent another unit': '+ Alquilar otra unidad',
  'Rent another unit': 'Alquilar otra unidad',
  'Pick a unit — you will sign the lease, then pay, and your gate code arrives by email.': 'Elija una unidad: firmará el contrato, luego pagará, y su código del portón llegará por correo electrónico.',
  'available': 'disponible(s)',
  'Choose unit…': 'Elegir unidad…',
  'Continue to lease': 'Continuar al contrato',
  'Starting…': 'Iniciando…',
  'No units are available right now — contact the office.': 'No hay unidades disponibles en este momento — contacte a la oficina.',
  'Give move-out notice': 'Avisar mudanza',
  'Cancel notice': 'Cancelar aviso',
  'Confirm notice': 'Confirmar aviso',
  'When do you plan to move out?': '¿Cuándo planea mudarse?',
  'Note (optional)': 'Nota (opcional)',
  'Close': 'Cerrar',

  // Payments tab
  'No payments found.': 'No se encontraron pagos.',
  'Date': 'Fecha',
  'Amount': 'Monto',
  'Method': 'Método',
  'Status': 'Estado',
  'Receipt': 'Recibo',
  'Download': 'Descargar',

  // Invoices tab
  'No invoices found.': 'No se encontraron facturas.',
  'Due': 'Vencimiento',
  'Total': 'Total',
  'Paid': 'Pagado',

  // Documents / Contact
  'Gate information': 'Información del portón',
  'No documents yet.': 'Aún no hay documentos.',
  'View': 'Ver',
  'Message the office': 'Enviar mensaje a la oficina',
  "We'll reply by email or text. For urgent gate or access issues, call the facility.":
    'Le responderemos por correo o mensaje de texto. Para problemas urgentes de acceso, llame a la oficina.',
  'Subject (optional)': 'Asunto (opcional)',
  'How can we help?': '¿Cómo podemos ayudarle?',
  'Send message': 'Enviar mensaje',
  'Sending…': 'Enviando…',
  'Sent messages': 'Mensajes enviados',
  'Message sent — the office will get back to you.':
    'Mensaje enviado — la oficina se comunicará con usted.',

  // Pay flow
  'Pay now': 'Pagar ahora',
  'Pay': 'Pagar',
  'Processing…': 'Procesando…',
  'Payment received': 'Pago recibido',
  'Back to dashboard': 'Volver al panel',
  'Back': 'Volver',
};

let lang = (() => {
  try { return localStorage.getItem(LS_KEY) || 'en'; } catch { return 'en'; }
})();
const listeners = new Set();

export function setLang(next) {
  lang = next === 'es' ? 'es' : 'en';
  try { localStorage.setItem(LS_KEY, lang); } catch { /* noop */ }
  listeners.forEach((fn) => fn());
}

export function getLang() { return lang; }

export function t(text) {
  if (lang === 'es') return ES[text] || text;
  return text;
}

// Re-render subscribers when the language flips.
export function useLang() {
  return useSyncExternalStore(
    (cb) => { listeners.add(cb); return () => listeners.delete(cb); },
    () => lang,
  );
}

// Small pill toggle for portal headers.
export function LangToggle() {
  const current = useLang();
  return (
    <button
      onClick={() => setLang(current === 'es' ? 'en' : 'es')}
      className="text-[12px] font-medium text-slate-400 hover:text-slate-600 border border-slate-200 rounded-full px-2.5 py-0.5"
      title={current === 'es' ? 'Switch to English' : 'Cambiar a español'}
    >
      {current === 'es' ? 'EN' : 'ES'}
    </button>
  );
}
